import React, { useMemo, useState } from "react";
import { apiFetch } from "../../../core/utils/api";
import { APP_DOMAIN, APP_URL_WWW } from "../../../core/utils/config";
import { PrimaryButton, SecondaryButton, EmptyState } from "../../../design-system";
import { typography, surfaces, borders, textColors, interactive } from "../../../design-system/tokens";

const MessageDetail = ({ message, onUpdateMessage, userContainers }) => {
  const [loading, setLoading] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  const isSystem = message?.kind === "system";

  const renderBoldOnly = (text) => {
    const s = String(text || "");
    const segments = [];
    let buf = "";
    let bold = false;
    for (let i = 0; i < s.length; ) {
      if (s[i] === "*" && s[i + 1] === "*") {
        if (buf) {
          segments.push({ text: buf, bold });
          buf = "";
        }
        bold = !bold;
        i += 2;
      } else {
        buf += s[i];
        i += 1;
      }
    }
    if (buf) segments.push({ text: buf, bold });
    return segments.map((seg, idx) =>
      seg.bold ? (
        <strong key={idx}>{seg.text}</strong>
      ) : (
        <React.Fragment key={idx}>{seg.text}</React.Fragment>
      )
    );
  };

  const systemBody = useMemo(() => String(message?.body_markdown || ""), [message?.body_markdown]);
  const systemLines = useMemo(() => systemBody.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n"), [systemBody]);

  if (!message) {
    return (
      <div className="h-full flex items-center justify-center">
        <EmptyState
          icon={
            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
            </svg>
          }
          title="Select a message"
          description="Choose a message from the list to view its details"
        />
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString([], {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  const parseUid = (uid) => {
    const s = String(uid || "");
    const [kind, raw] = s.split(":");
    const rawId = raw != null ? parseInt(raw, 10) : null;
    return { kind, rawId };
  };

  const handleUpdateField = async (field, value) => {
    setLoading(true);
    try {
      const { kind, rawId } = parseUid(message?.id);
      if (!rawId) return;
      const endpoint = kind === "system" ? `/api/inbox/messages/${rawId}/` : `/api/contact-messages/${rawId}/`;
      const body = kind === "system" ? { is_read: value } : { [field]: value };

      const response = await apiFetch(endpoint, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
      });

      if (response.ok) {
        const updatedMessage = await response.json();
        if (kind === "system") {
          onUpdateMessage({ ...message, is_read: !!updatedMessage?.is_read });
        } else {
          onUpdateMessage({ ...updatedMessage, kind: "contact", raw_id: updatedMessage.id, id: `contact:${updatedMessage.id}` });
        }
      } else {
        console.error("Failed to update message");
      }
    } catch (err) {
      console.error("Error updating message:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleReadToggle = async () => {
    await handleUpdateField('is_read', !message.is_read);
  };

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(message.sender_email);
      setEmailCopied(true);
      // Reset the check icon after 2 seconds
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  const getWebsiteInfo = () => {
    const container = userContainers.find(c => c.id === message.website_container);
    if (container) {
      return {
        name: container.name,
        path: `${message.owner_username}/${container.domain}`
      };
    }
    return {
      name: message.website_container_name,
      path: `${message.owner_username}/${message.website_container_domain}`
    };
  };

  const websiteInfo = !isSystem ? getWebsiteInfo() : null;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`border-b ${borders.default} ${surfaces.secondary} p-6`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h2 className={`${typography.h4} ${textColors.primary} mb-2`}>
              {isSystem ? `Message from "${message.sender_display_name || "MyVitae"}"` : `Message from "${message.sender_display_name}"`}
            </h2>
          </div>
          
          {/* Controls */}
          <div className="flex items-center space-x-3">
            {/* Read/Unread toggle */}
            {message.is_read ? (
              <SecondaryButton
                onClick={handleReadToggle}
                disabled={loading}
                size="sm"
              >
                Mark Unread
              </SecondaryButton>
            ) : (
              <PrimaryButton
                onClick={handleReadToggle}
                disabled={loading}
                size="sm"
              >
                Mark Read
              </PrimaryButton>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className={`space-y-1 ${typography.bodySmall} ${textColors.secondary}`}>
          {!isSystem && message.sender_email && (
            <div className="flex items-center">
              <span className={`font-medium mr-2 ${textColors.primary}`}>Email:</span>
              <a
                href={`mailto:${message.sender_email}`}
                className={`${interactive.link} ${interactive.linkHover}`}
              >
                {message.sender_email}
              </a>
              <button
                onClick={handleCopyEmail}
                className={`ml-2 p-1 ${textColors.tertiary} ${textColors.hover} transition-colors`}
                title="Copy email"
              >
                {emailCopied ? (
                  <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          )}
          {!isSystem && message.sender_phone && (
            <div className="flex items-center">
              <span className={`font-medium mr-2 ${textColors.primary}`}>Phone:</span>
              <a
                href={`tel:${message.sender_phone}`}
                className={`${interactive.link} ${interactive.linkHover}`}
              >
                {message.sender_phone}
              </a>
            </div>
          )}
          {!isSystem && websiteInfo && (
            <div className="flex items-center">
              <span className={`font-medium mr-2 ${textColors.primary}`}>Website:</span>
              <span className={`font-medium ${textColors.primary}`}>{websiteInfo.name}</span>
              <span className={`ml-1 ${textColors.tertiary}`}>
                (
                <a 
                  href={`${APP_URL_WWW}/${websiteInfo.path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${interactive.link} ${interactive.linkHover}`}
                >
                  {`${APP_DOMAIN}/${websiteInfo.path}`}
                </a>
                )
              </span>
            </div>
          )}
          <div className="flex items-center">
            <span className={`font-medium mr-2 ${textColors.primary}`}>Received:</span>
            <span className={textColors.secondary}>{formatDate(message.created_at)}</span>
          </div>
        </div>
      </div>

      {/* Message content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="prose max-w-none">
          <div className={`${surfaces.secondary} p-4 rounded-lg border ${borders.default}`}>
            <h3 className={`${typography.h5} ${textColors.primary} mb-3`}>{isSystem ? "Message" : "Message"}</h3>
            {isSystem ? (
              <div className={`${typography.body} ${textColors.primary}`}>
                {message.title ? (
                  <div className={`${typography.h5} ${textColors.primary} mb-3 font-semibold`}>
                    {message.title}
                  </div>
                ) : null}
                {systemLines.map((line, idx) => (
                  <React.Fragment key={idx}>
                    {renderBoldOnly(line)}
                    {idx < systemLines.length - 1 ? <br /> : null}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div className={`${typography.body} ${textColors.primary} whitespace-pre-wrap`}>
                {message.message}
              </div>
            )}
          </div>
        </div>
      </div>


    </div>
  );
};

export default MessageDetail; 